import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiClose, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command'
import { useCan } from '../../contexts/PermissionsContext'
import { useAddContactService, useContactServices, useRemoveContactService } from '../../hooks/useScenariosApi'

/**
 * The services a contact holds (spec 2026-10-28-15 § 4; moved out of the header
 * by spec 2026-11-03-02): a `<dt>/<dd>` row of the identity card, like Type and
 * Code. Empty, it shows a muted "—" (plus an add control for `contacts.write`);
 * a reader sees nothing at all when the contact holds no service. Editing is the
 * chips-plus-picker pattern of the tags section.
 */
export function ContactServicesField({ orgCode, contactUid }: { orgCode: string; contactUid: string }) {
    const { t } = useTranslation('scenarios')
    const canWrite = useCan('contacts.write')
    const { data } = useContactServices(orgCode, contactUid)
    const add = useAddContactService(orgCode, contactUid)
    const remove = useRemoveContactService(orgCode, contactUid)
    const [open, setOpen] = useState(false)
    const held = data?.data ?? []
    const vocabulary = data?.vocabulary ?? []
    const free = vocabulary.filter((s) => !held.some((h) => h.service_code === s.code && !h.scope_asset_uid))
    if (held.length === 0 && !canWrite) return null
    const label = (code: string) => t(`services.${code}`, { defaultValue: code })
    return (
        <div data-testid="contact-services">
            <dt className="text-sm font-medium text-muted-foreground">{t('contact_services.title')}</dt>
            <dd className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-foreground">
                {held.length === 0 && <span className="text-muted-foreground" data-testid="contact-services-empty">—</span>}
                {held.map((s) => (
                    <Badge key={s.uid} variant="outline" className="gap-1" data-testid={`contact-service-${s.service_code}`}>
                        {label(s.service_code)}
                        {s.scope_asset_name && <span className="text-muted-foreground">· {s.scope_asset_name}</span>}
                        {canWrite && (
                            <button type="button" onClick={() => remove.mutate(s.uid)} aria-label={t('contact_services.remove')} data-testid={`contact-service-remove-${s.service_code}`}>
                                <AppIcon path={mdiClose} size="xs" />
                            </button>
                        )}
                    </Badge>
                ))}
                {canWrite && free.length > 0 && (
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                aria-label={t('contact_services.add')}
                                title={t('contact_services.add')}
                                className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                data-testid="contact-service-add"
                            >
                                <AppIcon path={mdiPlus} size="xs" />
                                {t('contact_services.add_short')}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-0" align="start">
                            <Command>
                                <CommandInput placeholder={t('contact_services.search')} data-testid="contact-service-search" />
                                <CommandList>
                                    <CommandEmpty>{t('contact_services.none_found')}</CommandEmpty>
                                    <CommandGroup>
                                        {free.map((s) => (
                                            <CommandItem
                                                key={s.code}
                                                value={`${s.code} ${label(s.code)}`}
                                                onSelect={() => {
                                                    add.mutate({ service_code: s.code })
                                                    setOpen(false)
                                                }}
                                                data-testid={`contact-service-option-${s.code}`}
                                            >
                                                {label(s.code)}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                )}
            </dd>
        </div>
    )
}
